import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import LeadPage from '../../pages/LeadsPage/LeadPage/LeadPage'

/** servicios */
import {
  getLeadsPaginado,
  getLeadsIds,
  putLeads,
  getLeadById,
} from '../../services/leadsService'
import { getVehiculos } from '../../services/vehiculosService'
import { getDepartamentos } from '../../services/leadDepartamentosService'
import { getMunicipios } from '../../services/leadMunicipiosService'
import { getMarcas } from '../../services/leadMarcasService'

/**
 * Formatea una fecha a 'YYYY-MM-DD' en hora LOCAL (no UTC).
 * Evita el corrimiento de un dia que produce date.toISOString() en zonas UTC-negativas (Colombia UTC-5).
 */
const formatLocalDate = (date) => {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const LeadsContainer = () => {
  const hoy = new Date()
  const semanaEnMilisegundos = 24 * 60 * 60 * 1000 * 7
  const fechaDentroDeUnaSemana = new Date(hoy.getTime() - semanaEnMilisegundos)
  const initFechaInicial = formatLocalDate(fechaDentroDeUnaSemana)
  const initFechaFinal = formatLocalDate(new Date())

  const [selectedDateInit, setSelectedDateInit] = useState(initFechaInicial)
  const [selectedDateEnd, setSelectedDateEnd] = useState(initFechaFinal)
  const [ciudades, setCiudades] = useState([])
  const [vehiculos, setVehiculos] = useState([])
  const [marcas, setMarcas] = useState([])
  const [departamentos, setDepartamentos] = useState([])
  const [searchCiudad, setSearchCiudad] = useState([])
  const [openDetails, setOpenDetails] = useState(false)
  const [listResult, setListResult] = useState()
  const [openDetailsPopup, setOpenDetailsPopup] = useState(false)
  const [details, setDetails] = useState({})
  const [detailsPopup, setDetailsPopup] = useState([])
  const [isSelection] = useState(true)
  const [hiddenIntegration] = useState(false)
  const [loadingPopups, setLoadingPopups] = useState(false)
  // true cuando el usuario eligio "seleccionar todos los filtrados" (cross-page).
  // En ese caso rowSelection trae los ids de TODAS las paginas, no solo la cargada.
  const [allFilteredSelected, setAllFilteredSelected] = useState(false)
  // contadores del encabezado (vienen del backend, no se calculan en cliente)
  const [counters, setCounters] = useState({ total: 0, integrados: 0, no_integrados: 0 })

  /*= =======================================================================
   Estado de la tabla. Vive aqui (no dentro de la tabla) para que la seleccion
   sobreviva los cambios de pagina y para que la paginacion nunca se desincronice
   de lo que respondio el backend.
  ======================================================================== */
  const [rows, setRows] = useState([])
  const [rowCount, setRowCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 25 })
  // globalFilter = lo que el usuario teclea; debouncedFilter = lo que se manda al backend
  const [globalFilter, setGlobalFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  // seleccion como mapa { [id]: true }; la llave es el id del lead, no el indice de fila,
  // por eso no se pierde al paginar ni al recargar
  const [rowSelection, setRowSelection] = useState({})
  // se incrementa para forzar una recarga cuando cambian filtros/fechas
  const [queryVersion, setQueryVersion] = useState(0)

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id]),
    [rowSelection],
  )
  const qtyLeads = selectedIds.length

  // parametros vigentes de consulta (fechas + filtros); fuente de verdad para la carga
  const queryRef = useRef({
    fecha_inicial: initFechaInicial,
    fecha_final: initFechaFinal,
    marca: null,
    ciudad: null,
    vehiculo: null,
    departamento: null,
    integrado_impulsa: null,
    testdrive: null,
  })

  /** recarga la pagina actual sin tocar paginacion ni seleccion */
  const reload = () => setQueryVersion((v) => v + 1)

  /**
   * Cambio de filtros/fechas: vuelve a la pagina 1, limpia la seleccion (ya no corresponde
   * al nuevo universo filtrado) y recarga.
   */
  const refetch = () => {
    setAllFilteredSelected(false)
    setRowSelection({})
    setPagination((p) => ({ ...p, pageIndex: 0 }))
    reload()
  }

  /** debounce de la busqueda: se teclea libre, se consulta 500ms despues */
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilter(globalFilter || ''), 500)
    return () => clearTimeout(timer)
  }, [globalFilter])

  /** una busqueda nueva empieza en la pagina 1 */
  useEffect(() => {
    setPagination((p) => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }))
  }, [debouncedFilter])

  /**
   * Carga una pagina del backend. El flag `cancelled` descarta respuestas viejas: si el
   * usuario pasa paginas rapido, la respuesta lenta de la pagina anterior ya no sobreescribe
   * la nueva (eso era parte del "se buguea" al llegar al final).
   */
  useEffect(() => {
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      const q = queryRef.current
      // se guarda la busqueda vigente para reusarla en getLeadsIds
      queryRef.current.busqueda = debouncedFilter
      const r = await getLeadsPaginado({
        fecha_inicial: q.fecha_inicial,
        fecha_final: q.fecha_final,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        busqueda: debouncedFilter,
        marca: q.marca || '',
        ciudad: q.ciudad || '',
        departamento: q.departamento || '',
        vehiculo: q.vehiculo || '',
        integrado_impulsa: q.integrado_impulsa || '',
        testdrive: q.testdrive || '',
      })
      if (cancelled) return
      if (r && r.success) {
        setRows(r.data || [])
        setRowCount(r.total ?? 0)
        setCounters({
          total: r.total_filtrado,
          integrados: r.integrados,
          no_integrados: r.no_integrados,
        })
      } else {
        setRows([])
        setRowCount(0)
      }
      setIsLoading(false)
    }

    load()
    return () => {
      cancelled = true
    }
  }, [pagination.pageIndex, pagination.pageSize, debouncedFilter, queryVersion])

  /**
   * Si el total baja (por filtros o por integrar) y la pagina actual quedo fuera de rango,
   * se corrige a la ultima pagina valida en vez de quedar en una pagina vacia.
   */
  useEffect(() => {
    if (isLoading) return
    const lastPage = Math.max(0, Math.ceil(rowCount / pagination.pageSize) - 1)
    if (pagination.pageIndex > lastPage) {
      setPagination((p) => ({ ...p, pageIndex: lastPage }))
    }
  }, [rowCount, isLoading, pagination.pageSize, pagination.pageIndex])

  /** filtros (Autocomplete/Select) -> actualizan la consulta y refrescan la tabla */
  const handleChange = (key, value) => {
    queryRef.current = { ...queryRef.current, [key]: value }
    refetch()
  }

  const handleChangeDepartment = (key, value) => {
    // al cambiar departamento se resetea la ciudad seleccionada
    queryRef.current = { ...queryRef.current, [key]: value, ciudad: null }
    const result = key === 'departamento' && value
      ? ciudades?.filter((item) => item.NOMBRE_DPT === value)
      : []
    setSearchCiudad(result)
    refetch()
  }

  const handleDateInitChange = (date) => {
    const fecha = formatLocalDate(date)
    setSelectedDateInit(fecha)
    queryRef.current = { ...queryRef.current, fecha_inicial: fecha }
    refetch()
  }

  const handleDateEndChange = (date) => {
    const fecha = formatLocalDate(date)
    setSelectedDateEnd(fecha)
    queryRef.current = { ...queryRef.current, fecha_final: fecha }
    refetch()
  }

  /*= =======================================================================
   Catalogos
  ======================================================================== */
  const fetchDepartamentos = async () => {
    const { success, data } = await getDepartamentos()
    if (success) {
      setDepartamentos(data)
    } else {
      console.log('error')
    }
  }

  const fetchCiudades = async () => {
    const { success, data } = await getMunicipios()
    if (success) {
      const sortCity = data.sort((a, b) => {
        if (a.NOMBRE_MPI > b.NOMBRE_MPI) return 1
        if (a.NOMBRE_MPI < b.NOMBRE_MPI) return -1
        return 0
      })
      setCiudades(sortCity)
    } else {
      console.log('error')
    }
  }

  const fetchVehiculos = async () => {
    const { success, data } = await getVehiculos()
    if (success) {
      setVehiculos(data)
    } else {
      console.log('error')
    }
  }

  const fetchMarcas = async () => {
    const { success, data } = await getMarcas()
    if (success) {
      setMarcas(data)
    } else {
      console.log('error')
    }
  }

  // estable: las columnas de la tabla dependen de esta funcion; si cambiara en cada render
  // material-react-table reconstruiria las columnas en cada render
  const onOpenDetails = useCallback((rowData) => {
    setOpenDetails(true)
    setDetails(rowData)
  }, [])

  const onOpenDetailsPopup = async (id) => {
    setLoadingPopups(true)
    const { success, data } = await getLeadById(id)
    if (success == 1) {
      setListResult(data)
    } else {
      console.log('error')
    }
    setLoadingPopups(false)
  }

  const onCloseDetails = () => {
    setOpenDetails(false)
  }

  const onCloseDetailsPopupResult = () => {
    setOpenDetailsPopup(false)
  }

  /**
   * Seleccion manual (checkbox). Recibe el updater de material-react-table y lo aplica
   * sobre el mapa completo, por eso conserva los ids marcados en otras paginas.
   */
  const onRowSelectionChange = (updater) => {
    setAllFilteredSelected(false)
    setRowSelection((prev) => (typeof updater === 'function' ? updater(prev) : updater))
  }

  /**
   * "Seleccionar TODOS los leads filtrados" (cross-page).
   * Trae del backend solo los ids de los no integrados que cumplen los filtros vigentes
   * y los deja como seleccion a integrar. integrado_impulsa='NO' => solo integrables.
   */
  const onSelectAllFiltered = async () => {
    const q = queryRef.current
    const r = await getLeadsIds({
      fecha_inicial: q.fecha_inicial,
      fecha_final: q.fecha_final,
      busqueda: q.busqueda || '',
      marca: q.marca || '',
      ciudad: q.ciudad || '',
      departamento: q.departamento || '',
      vehiculo: q.vehiculo || '',
      integrado_impulsa: 'NO',
      testdrive: q.testdrive || '',
    })
    if (r && r.success) {
      // mapa { id: true } con todos los ids filtrados; las filas de la pagina cargada
      // aparecen marcadas automaticamente porque la tabla indexa por id
      const seleccion = {}
      r.ids.forEach((id) => {
        seleccion[String(id)] = true
      })
      setRowSelection(seleccion)
      setAllFilteredSelected(true)
    }
  }

  /** limpia cualquier seleccion (pagina o todos-filtrados); ya no necesita recargar */
  const onClearSelection = () => {
    setAllFilteredSelected(false)
    setRowSelection({})
  }

  const onIntegrate = async () => {
    if (!selectedIds.length) return
    const success = await putLeads(selectedIds)

    if (success && success.success == 1) {
      let result = []
      try {
        result = JSON.parse(success.response)
      } catch (err) {
        console.error('onIntegrate: respuesta no es JSON válido', err)
      }
      setOpenDetailsPopup(true)
      setDetailsPopup(result)
      setAllFilteredSelected(false)
      setRowSelection({})
      // recargar la pagina actual con los estados ya integrados (sin perder la pagina)
      reload()
    } else {
      console.log('error')
    }
  }

  useEffect(() => {
    fetchCiudades()
    fetchVehiculos()
    fetchMarcas()
    fetchDepartamentos()
  }, [])

  return (
    <LeadPage
      counters={counters}
      rows={rows}
      rowCount={rowCount}
      isLoading={isLoading}
      pagination={pagination}
      onPaginationChange={setPagination}
      globalFilter={globalFilter}
      onGlobalFilterChange={setGlobalFilter}
      rowSelection={rowSelection}
      onRowSelectionChange={onRowSelectionChange}
      vehiculos={vehiculos}
      ciudades={searchCiudad}
      marcas={marcas}
      openDetails={openDetails}
      openDetailsPopup={openDetailsPopup}
      onOpenDetailsPopup={onOpenDetailsPopup}
      handleChange={handleChange}
      departamentos={departamentos}
      hiddenIntegration={hiddenIntegration}
      onOpenDetails={onOpenDetails}
      onCloseDetails={onCloseDetails}
      onCloseDetailsPopupResult={onCloseDetailsPopupResult}
      onIntegrate={onIntegrate}
      handleChangeDepartment={handleChangeDepartment}
      isSelection={isSelection}
      details={details}
      detailsPopup={detailsPopup}
      listResult={listResult}
      onSelectAllFiltered={onSelectAllFiltered}
      onClearSelection={onClearSelection}
      allFilteredSelected={allFilteredSelected}
      handleDateInitChange={handleDateInitChange}
      handleDateEndChange={handleDateEndChange}
      selectedDateInit={selectedDateInit}
      selectedDateEnd={selectedDateEnd}
      qtyLeads={qtyLeads}
      loadingPopups={loadingPopups}
    />
  )
}

export default LeadsContainer
